import {
  ASSIGNMENT_STATUSES,
  createAssignmentSchema,
  type CreateAssignmentInput,
} from '@hirebotiq-ops/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { AdminLayout } from '../components/AdminLayout.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { Field } from '../components/ui/field.js';
import { Input } from '../components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/assignments/')({
  component: AdminAssignments,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

type StatusFilter = 'all' | (typeof ASSIGNMENT_STATUSES)[number];
type ViewMode = 'table' | 'calendar';

function AdminAssignments() {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [creating, setCreating] = useState(false);
  const list = trpc.assignments.list.useQuery(
    status === 'all' ? {} : { status },
  );

  return (
    <AdminLayout>
      <div className="space-y-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-bold">Einsätze (Disposition)</h1>
          <div className="flex gap-2">
            <div className="inline-flex rounded-md border border-[var(--border)] p-1 bg-[var(--muted)]/20">
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
              >
                Tabelle
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('calendar')}
              >
                Kalender-Übersicht
              </Button>
            </div>
            <Button
              variant={creating ? 'ghost' : 'default'}
              size="sm"
              onClick={() => setCreating((v) => !v)}
            >
              {creating ? 'Schließen' : '+ Neuer Einsatz'}
            </Button>
          </div>
        </header>

        {creating && (
          <Card>
            <CardHeader>
              <CardTitle>Neuen Einsatz anlegen</CardTitle>
              <CardDescription>Worker × Kunde × Zeitraum (auditiert, mit Live-Kollisionsprüfung)</CardDescription>
            </CardHeader>
            <CardContent>
              <CreateAssignmentForm onCreated={() => setCreating(false)} />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Filter</CardTitle>
          </CardHeader>
          <CardContent className="max-w-xs">
            <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                {ASSIGNMENT_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {viewMode === 'table' ? (
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Worker</TableHead>
                    <TableHead>Kunde</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Zeitraum</TableHead>
                    <TableHead>Rolle</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.isLoading && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                        Lade …
                      </TableCell>
                    </TableRow>
                  )}
                  {list.data?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                        Keine Einsätze.
                      </TableCell>
                    </TableRow>
                  )}
                  {list.data?.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>
                        <Link
                          to="/admin/assignments/$id"
                          params={{ id: a.id }}
                          className="hover:underline font-medium"
                        >
                          {[a.workerFirstName, a.workerLastName].filter(Boolean).join(' ') ||
                            '(unbenannt)'}
                        </Link>
                      </TableCell>
                      <TableCell>{a.clientCompanyName ?? '—'}</TableCell>
                      <TableCell>
                        <StatusBadge status={a.status} />
                      </TableCell>
                      <TableCell className="text-xs text-[var(--muted-foreground)]">
                        {a.startDate ?? '—'} – {a.endDate ?? 'offen'}
                      </TableCell>
                      <TableCell className="text-xs">{a.roleDescription ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          /* Kalender-/Zeitstrahl-Übersicht */
          <Card>
            <CardHeader>
              <CardTitle>Disposition nach Worker &amp; Zeitraum</CardTitle>
              <CardDescription>
                Chronologische Einsatz-Visualisierung aller aktiven und geplanten Zuweisungen.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {list.isLoading && <p className="text-sm text-[var(--muted-foreground)]">Lade Dispo …</p>}
              {list.data?.length === 0 && (
                <p className="text-sm text-[var(--muted-foreground)]">Keine Einsätze für die Kalender-Ansicht.</p>
              )}
              {list.data && list.data.length > 0 && (
                <div className="grid gap-3 md:grid-cols-2">
                  {list.data.map((a) => (
                    <div
                      key={a.id}
                      className="rounded-md border border-[var(--border)] p-4 bg-[var(--card)] space-y-2 hover:border-[var(--ring)] transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <Link
                          to="/admin/assignments/$id"
                          params={{ id: a.id }}
                          className="font-bold hover:underline"
                        >
                          {[a.workerFirstName, a.workerLastName].filter(Boolean).join(' ') || '(unbenannt)'}
                        </Link>
                        <StatusBadge status={a.status} />
                      </div>
                      <div className="text-sm">
                        <span className="font-medium text-foreground">Kunde:</span>{' '}
                        {a.clientCompanyName ?? '—'}
                      </div>
                      <div className="text-xs text-[var(--muted-foreground)] flex items-center justify-between">
                        <span>
                          📅 {a.startDate ?? 'Start unbestimmt'} bis {a.endDate ?? 'offen'}
                        </span>
                        {a.entgeltGroup && <span className="font-semibold">EG: {a.entgeltGroup}</span>}
                      </div>
                      {a.worksiteAddress && (
                        <div className="text-xs text-[var(--muted-foreground)]">
                          📍 {a.worksiteAddress}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}

function CreateAssignmentForm({ onCreated }: { onCreated: () => void }) {
  const utils = trpc.useUtils();
  const workers = trpc.workers.list.useQuery({ limit: 500 });
  const clients = trpc.clients.list.useQuery({ limit: 500 });
  const create = trpc.assignments.create.useMutation({
    onSuccess: () => {
      toast.success('Einsatz angelegt.');
      utils.assignments.list.invalidate();
      onCreated();
    },
    onError: (e) => toast.error(e.message),
  });

  const form = useForm<CreateAssignmentInput>({
    resolver: zodResolver(createAssignmentSchema),
    defaultValues: {},
  });
  const { register, handleSubmit, setValue, watch, formState } = form;

  const selectedWorkerId = watch('workerId');
  const selectedStartDate = watch('startDate');
  const selectedEndDate = watch('endDate');

  // Live-Check für Konflikte
  const conflictCheck = trpc.assignments.checkConflicts.useQuery(
    {
      workerId: selectedWorkerId ?? '',
      startDate: selectedStartDate || undefined,
      endDate: selectedEndDate || undefined,
    },
    { enabled: !!selectedWorkerId && (!!selectedStartDate || !!selectedEndDate) },
  );

  const onSubmit = handleSubmit((values) => {
    const clean = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v !== '' && v != null),
    ) as CreateAssignmentInput;
    create.mutate(clean);
  });

  return (
    <form className="grid gap-4 md:grid-cols-2" onSubmit={onSubmit}>
      <Field label="Worker *" htmlFor="workerId" error={formState.errors.workerId?.message}>
        <Select value={watch('workerId')} onValueChange={(v) => setValue('workerId', v)}>
          <SelectTrigger id="workerId">
            <SelectValue placeholder="Worker wählen" />
          </SelectTrigger>
          <SelectContent>
            {workers.data?.map((w) => (
              <SelectItem key={w.id} value={w.id}>
                {[w.firstName, w.lastName].filter(Boolean).join(' ') || w.id.slice(0, 8)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Kunde *" htmlFor="clientId" error={formState.errors.clientId?.message}>
        <Select value={watch('clientId')} onValueChange={(v) => setValue('clientId', v)}>
          <SelectTrigger id="clientId">
            <SelectValue placeholder="Kunde wählen" />
          </SelectTrigger>
          <SelectContent>
            {clients.data?.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.companyName ?? c.id.slice(0, 8)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Start" htmlFor="startDate">
        <Input id="startDate" type="date" {...register('startDate')} />
      </Field>
      <Field label="Ende" htmlFor="endDate">
        <Input id="endDate" type="date" {...register('endDate')} />
      </Field>

      {/* Live Konflikt-Anzeige */}
      {conflictCheck.data &&
        (conflictCheck.data.hasAssignmentConflict || conflictCheck.data.hasAbsenceConflict) && (
          <div className="md:col-span-2 rounded-md bg-[oklch(0.95_0.08_85)] p-3 border border-[oklch(0.85_0.15_85)] space-y-2 text-sm text-[oklch(0.3_0.15_85)]">
            <p className="font-semibold flex items-center gap-2">
              ⚠️ Achtung: Terminüberschneidung erkannt!
            </p>
            {conflictCheck.data.hasAssignmentConflict && (
              <p>
                Der Worker ist im Zeitraum bereits bei{' '}
                {conflictCheck.data.conflictingAssignments
                  .map((a) => `${a.clientCompanyName ?? 'Kunde'} (${a.startDate} – ${a.endDate ?? 'offen'})`)
                  .join(', ')}{' '}
                verplant.
              </p>
            )}
            {conflictCheck.data.hasAbsenceConflict && (
              <p>
                Der Worker hat im Zeitraum Abwesenheiten (Urlaub/Krankheit):{' '}
                {conflictCheck.data.conflictingAbsences
                  .map((ab) => `${ab.type} (${ab.startDate} – ${ab.endDate}, Status: ${ab.status})`)
                  .join(', ')}.
              </p>
            )}
          </div>
        )}

      <Field label="Einsatzort" htmlFor="worksiteAddress">
        <Input id="worksiteAddress" {...register('worksiteAddress')} />
      </Field>
      <Field label="Rolle / Tätigkeit" htmlFor="roleDescription">
        <Input id="roleDescription" {...register('roleDescription')} />
      </Field>
      <div className="md:col-span-2 flex justify-end">
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Lege an …' : 'Einsatz anlegen'}
        </Button>
      </div>
    </form>
  );
}

